import { useCallback, useEffect, useState } from 'react';
import type { TargetTemplateInfo, EditedConfigInfo, TargetWithConfigs } from './types';
import {
  SelectionMap,
  ConflictMap,
  FuelChip,
  conflictKey,
  targetMatchesFilter,
} from './state';

export interface UsePlannerArgs {
  targetTemplates: TargetTemplateInfo[];
  editedConfigs: EditedConfigInfo[];
  sourceFuelType: string;
  open: boolean;
}

export function usePlanner({
  targetTemplates,
  editedConfigs,
  sourceFuelType,
  open,
}: UsePlannerArgs) {
  const [selection, setSelection] = useState<SelectionMap>(new Map());
  const [conflicts, setConflicts] = useState<ConflictMap>(new Map());
  const [filter, setFilter] = useState<FuelChip>('ALL');
  const [activeTargetKey, setActiveTargetKey] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Smart-default selection runs ONLY on open transition, not on every prop change.
  // Async-loaded targets that arrive later never overwrite user selection.
  useEffect(() => {
    if (!open) return;
    const initSel: SelectionMap = new Map();
    const fuel = (sourceFuelType || '').toUpperCase();
    for (const t of targetTemplates) {
      if (t.fuelType.toUpperCase() !== fuel) continue;
      const applicable = t.applicableConfigKeys ?? [];
      const notYetCopied = applicable.filter(k => {
        const cfg = editedConfigs.find(c => c.configKey === k);
        return !cfg?.copiedToTemplates?.some(ct => ct.toTemplateKey === t.templateKey);
      });
      if (notYetCopied.length > 0) initSel.set(t.templateKey, new Set(notYetCopied));
    }
    setSelection(initSel);
    setConflicts(new Map());
    setFilter('ALL');
    const firstKey = initSel.keys().next().value ?? targetTemplates[0]?.templateKey ?? null;
    setActiveTargetKey(firstKey);

    // Auto-expand all groups when filter is active so the user immediately sees content.
    const groups = new Set<string>();
    for (const t of targetTemplates) {
      groups.add(`${t.channel}::${t.fuelType.toUpperCase() || 'NONE'}`);
    }
    setExpandedGroups(groups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleTarget = useCallback(
    (tKey: string, applicable: string[]) => {
      setSelection(prev => {
        const next = new Map(prev);
        if (next.has(tKey)) {
          next.delete(tKey);
        } else {
          const notYetCopied = applicable.filter(k => {
            const cfg = editedConfigs.find(c => c.configKey === k);
            return !cfg?.copiedToTemplates?.some(ct => ct.toTemplateKey === tKey);
          });
          // Fall back to all applicable keys when everything is already copied so the
          // user can still explicitly force a re-copy by clicking the checkbox.
          next.set(tKey, new Set(notYetCopied.length > 0 ? notYetCopied : applicable));
        }
        return next;
      });
    },
    [editedConfigs],
  );

  const toggleConfig = useCallback((tKey: string, cKey: string) => {
    setSelection(prev => {
      const next = new Map(prev);
      const cur = new Set(next.get(tKey) ?? []);
      if (cur.has(cKey)) cur.delete(cKey);
      else cur.add(cKey);
      if (cur.size === 0) next.delete(tKey);
      else next.set(tKey, cur);
      return next;
    });
  }, []);

  const setConflictResolution = useCallback(
    (tKey: string, cKey: string, mode: 'overwrite' | 'keep') => {
      setConflicts(prev => {
        const next = new Map(prev);
        next.set(conflictKey(tKey, cKey), mode);
        return next;
      });
    },
    [],
  );

  // Bulk ops scope: ONLY visible (filter-matching) targets. Hidden state preserved.
  const selectAllVisible = useCallback(() => {
    const editedByKey = new Map(editedConfigs.map(c => [c.configKey, c]));
    setSelection(prev => {
      const next = new Map(prev);
      for (const t of targetTemplates) {
        if (!targetMatchesFilter(t, filter)) continue;
        const applicable = t.applicableConfigKeys ?? [];
        const notYetCopied = applicable.filter(k => {
          const cfg = editedByKey.get(k);
          return !cfg?.copiedToTemplates?.some(ct => ct.toTemplateKey === t.templateKey);
        });
        if (notYetCopied.length > 0) next.set(t.templateKey, new Set(notYetCopied));
      }
      return next;
    });
  }, [targetTemplates, filter, editedConfigs]);

  const clearVisibleSelection = useCallback(() => {
    setSelection(prev => {
      const next = new Map(prev);
      for (const t of targetTemplates) {
        if (!targetMatchesFilter(t, filter)) continue;
        next.delete(t.templateKey);
      }
      return next;
    });
  }, [targetTemplates, filter]);

  const expandAllGroups = useCallback(() => {
    const groups = new Set<string>();
    for (const t of targetTemplates) {
      groups.add(`${t.channel}::${t.fuelType.toUpperCase() || 'NONE'}`);
    }
    setExpandedGroups(groups);
  }, [targetTemplates]);

  const collapseAllGroups = useCallback(() => setExpandedGroups(new Set()), []);

  const toSelections = useCallback(
    (targets: TargetTemplateInfo[]): TargetWithConfigs[] => {
      const out: TargetWithConfigs[] = [];
      for (const t of targets) {
        const cfgSet = selection.get(t.templateKey);
        if (!cfgSet || cfgSet.size === 0) continue;
        const keys = Array.from(cfgSet).filter(
          k => conflicts.get(conflictKey(t.templateKey, k)) !== 'keep',
        );
        if (keys.length > 0) out.push({ target: t, configKeys: keys });
      }
      return out;
    },
    [selection, conflicts],
  );

  return {
    selection,
    conflicts,
    filter,
    activeTargetKey,
    expandedGroups,
    setFilter,
    setActiveTargetKey,
    setExpandedGroups,
    toggleTarget,
    toggleConfig,
    setConflictResolution,
    selectAllVisible,
    clearVisibleSelection,
    expandAllGroups,
    collapseAllGroups,
    toSelections,
  };
}

export type Planner = ReturnType<typeof usePlanner>;
